// components/LeadForms/ThreeLeadForm.tsx
'use client'

import { Spinner } from "@components/atoms";
import { getNYearsBack } from "@components/hooks";
import { generateId } from "../LeadForm/helper";
import { Text, TextInput } from "@mantine/core";
import classes from "styles/LeadFormStyles/LeadForm.module.css";
import React, { useEffect, useRef, useState } from 'react';
import dynamic from "next/dynamic";

export interface ThreeLeadFormProps {
    formName: string;
    isds: Record<string, string>[];
    getQuoteForm: any;
    onMobileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onDateChangeMobile?: (value: string) => void;
    onNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onMobileReset: () => void;
    onViewPlan: () => void;
    initData: any;
    constraint: any;
    isLoading: boolean;
    onCountryChange: (val: string) => void;
    errors: {
        phone?: string;
        name?: string;
        otp?: string;
        dob?: string;
    };
}

// Import components normally since we're using 'use client'
import PhoneInput from "@components/atoms/PhoneInput";
import LeadFormDisclaimer from "@components/atoms/EthinosHomeLeft/LeadFormDisclaimer";

const ThreeLeadForm: React.FC<ThreeLeadFormProps> = ({
    formName,
    isds,
    getQuoteForm,
    onMobileChange,
    onCountryChange,
    initData,
    onDateChange,
    onDateChangeMobile,
    onMobileReset,
    constraint,
    isLoading,
    errors,
    onNameChange,
    onViewPlan,
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const defaultCountry = "India";
    const isFormInvalid =
        !getQuoteForm?.phoneNumber ||
        !getQuoteForm?.name?.trim() ||
        !getQuoteForm?.dob ||
        !!errors.phone || !!errors.name || !!errors.dob;
    const dobRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Don't render until mounted to avoid hydration issues
    if (!isMounted) {
        return (
            <section className="bg-white" suppressHydrationWarning>
                <div className="flex flex-col gap-3" suppressHydrationWarning>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Mobile Number
                        </Text>
                        <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Name
                        </Text>
                        <div className="border border-gray-300 rounded-md px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="flex flex-col gap-0">
                        <Text component="label" fw={600} size="sm" className="mb-2">
                            Date of Birth
                        </Text>
                        <div className="border border-gray-300 rounded-[4px] px-3 py-2.5 bg-white h-10"></div>
                    </div>
                    <div className="w-full my-2">
                        <button
                            type="button"
                            disabled
                            className="bg-orange-300 text-white cursor-not-allowed rounded-md w-full px-10 py-3"
                        >
                            Get Quote
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-white">
            <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-0">
                    <Text
                        htmlFor={generateId("mobile-number-input", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Mobile Number
                    </Text>
                    <PhoneInput
                        isds={isds}
                        inputValue={getQuoteForm?.phoneNumber}
                        countryValue={getQuoteForm?.country || defaultCountry}
                        onMobileChange={onMobileChange}
                        onCountryChange={onCountryChange}
                        isMask={initData?.channel == "Tele Caller" || false}
                        onMobileReset={onMobileReset}
                        newDesignConfig={false}
                        isLeadForm
                    />
                    {errors.phone && (
                        <Text component="div" size="xs" color="red">
                            {errors.phone}
                        </Text>
                    )}
                </div>

                <div className="flex flex-col gap-0">
                    <Text
                        htmlFor={generateId("name-input", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Name
                    </Text>
                    <TextInput
                        placeholder="Enter your name"
                        size="sm"
                        radius="md"
                        classNames={classes}
                        className="rounded-md text-base"
                        value={getQuoteForm?.name}
                        onChange={onNameChange}
                        styles={{
                            input: {
                                fontSize: "1rem",
                                outline: "none",
                                boxShadow: "none",
                                '&:focus': {
                                    outline: "none",
                                    boxShadow: "none",
                                },
                            },
                        }}
                    />
                    {errors.name && (
                        <Text component="div" size="xs" color="red">
                            {errors.name}
                        </Text>
                    )}
                </div>

                <div className="flex flex-col gap-0">
                    <Text
                        htmlFor={generateId("dob-input", formName)}
                        component="label"
                        fw={600}
                        size="sm"
                        className="mb-2"
                    >
                        Date of Birth
                    </Text>

                    <div className="flex items-center gap-2 border border-gray-300 rounded-[4px] px-3 py-2.5 bg-white">
                        <input
                            type="date"
                            className="flex-1 text-base font-normal outline-none border-none bg-transparent"
                            required
                            onChange={onDateChange}
                            value={getQuoteForm?.dob}
                            min={getNYearsBack(constraint.max).add(1, "day").format("YYYY-MM-DD")}
                            max={getNYearsBack(constraint.min).format("YYYY-MM-DD")}
                            ref={dobRef}
                        />
                    </div>

                    {errors.dob && (
                        <Text size="xs" color="red" component="div">
                            {errors.dob}
                        </Text>
                    )}
                </div>

                <LeadFormDisclaimer />

                <div className="w-full my-2">
                    <button
                        type="submit"
                        disabled={isFormInvalid || isLoading}
                        className={`rounded-md w-full px-10 py-2 font-semibold transition-opacity ${isFormInvalid || isLoading
                            ? "bg-orange-300 text-white cursor-not-allowed"
                            : "bg-nivabupa-ethinosHomeOrange text-white hover:opacity-90"}`}
                        onClick={onViewPlan}
                    >
                        {isLoading ? <Spinner /> : "Get Quote"}
                    </button>
                </div>
            </div>
        </section>
    );
}

export default ThreeLeadForm;